import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Globe, Upload, Plug, Check, Loader2, Trash2, Download, Sparkles, FileText, Link2 } from "lucide-react";

const KIND_LABEL = { business_plan: "Business plan", blueprint: "Blueprint / tech spec", balance_sheet: "Balance Sheet", profit_loss: "Profit & Loss", forecast: "Forecast / model", cap_table: "Cap table", document: "Document", website: "Website", integration: "Integration", other: "Other" };

export default function DataHub({ onSuggest, compact = false }) {
  const [url, setUrl] = useState("");
  const [items, setItems] = useState([]);
  const [providers, setProviders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [kind, setKind] = useState("business_plan");
  const [sugg, setSugg] = useState(null);
  const [showConn, setShowConn] = useState(null);
  const [connForm, setConnForm] = useState({ account_label: "", credentials: {} });
  const [oauthInfo, setOauthInfo] = useState(null);
  const [oauthBusy, setOauthBusy] = useState(null);

  const load = async () => {
    try {
      const [i, p] = await Promise.all([api.get("/intel"), api.get("/integrations")]);
      setItems(i.data); setProviders(p.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const inspect = async () => {
    setErr(""); setOk(""); setBusy(true);
    try { await api.post("/intel/inspect", { url }); setOk("Website inspected — signals added to your knowledge base."); setUrl(""); await load(); }
    catch (e) { setErr(e.response?.data?.detail || "Inspection failed. Check the URL and retry."); }
    finally { setBusy(false); }
  };
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr(""); setOk(""); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const qs = `?kind=${encodeURIComponent(kind)}`;
      const { data } = await api.post(`/intel/upload${qs}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const n = data?.analysis?.line_items ? Object.keys(data.analysis.line_items).length : 0;
      setOk(`${f.name} parsed — ${data?.analysis?.doc_type || "document"} · ${n} line items detected.`);
      await load();
    } catch (e2) { setErr(e2.response?.data?.detail || "Upload failed."); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const remove = async (id) => { await api.delete(`/intel/${id}`); load(); };
  const suggest = async () => {
    setErr("");
    try {
      const { data } = await api.get("/intel/suggest-general");
      setSugg(data);
      if (onSuggest && data?.suggested) onSuggest(data.suggested, data);
    } catch { setErr("No suggestions yet — add a website or upload statements first."); }
  };
  const connect = async (pid) => {
    setErr(""); setOk("");
    try {
      await api.post("/integrations/connect", { provider_id: pid, account_label: connForm.account_label, credentials: connForm.credentials });
      setOk("Connected. Import statements via upload/CSV, then apply suggestions.");
      setShowConn(null); setConnForm({ account_label: "", credentials: {} }); load();
    } catch (e) { setErr(e.response?.data?.detail || "Connect failed."); }
  };
  const sync = async (pid) => {
    try {
      const { data } = await api.post(`/integrations/${pid}/sync`);
      if (data.mode === "live" && data.pulled) {
        const kv = Object.entries(data.pulled).filter(([k]) => k !== "raw_note").map(([k, v]) => `${k}: ${Number(v).toLocaleString()}`).join(" · ");
        setOk(`Live pull ✓ ${kv || data.note}`);
      } else setOk(`Synced — available: ${(data.available_docs || []).join(", ")}. ${data.note || ""}`);
      load();
    }
    catch { setErr("Sync failed."); }
  };
  const oauthGo = async (pid) => {
    setErr(""); setOauthBusy(pid);
    try {
      const { data } = await api.get(`/integrations/${pid}/oauth/start`);
      if (data.mode === "oauth" && data.authorize_url) { window.location.href = data.authorize_url; return; }
      setOauthInfo({ pid, ...data });
    } catch { setErr("Could not start OAuth."); }
    finally { setOauthBusy(null); }
  };
  const disconnect = async (pid) => { await api.delete(`/integrations/${pid}`); load(); };

  const lastUpload = items.find((it) => it.analysis && (it.analysis.flags || it.analysis.unit || it.analysis.periods));

  return (
    <div className="space-y-4">
      {err && <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-[13px] px-4 py-2.5" role="alert">{err}</div>}
      {ok && <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-[13px] px-4 py-2.5">{ok}</div>}

      {/* website inspector */}
      <div className="card-vs p-5">
        <div className="flex items-center gap-2 font-semibold text-[15px]"><Globe size={17} className="text-[#174C82]" /> Inspect website</div>
        <p className="text-[13px] text-slate-500 mt-1">We fetch your public site and extract positioning, model and traction signals into the valuation knowledge base.</p>
        <div className="flex gap-2 mt-3">
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="acme.com or https://acme.com" className="flex-1 h-11 rounded-lg border border-slate-300 px-3 text-[14px] focus:border-[#174C82] focus:ring-2 focus:ring-[#F5C86B] focus:outline-none" data-testid="intel-url-input" />
          <button onClick={inspect} disabled={busy || !url} className="btn-primary-vs h-11 px-5 font-semibold flex items-center gap-1.5 text-[14px]" data-testid="intel-inspect-button">{busy ? <Loader2 size={15} className="animate-spin" /> : <Globe size={15} />} Inspect</button>
        </div>
      </div>

      {/* uploads */}
      <div className="card-vs p-5">
        <div className="flex items-center gap-2 font-semibold text-[15px]"><Upload size={17} className="text-[#174C82]" /> Upload business plans, blueprints & statements</div>
        <p className="text-[13px] text-slate-500 mt-1">Business plans, technical blueprints, <b>Balance Sheets</b> and <b>Profit & Loss</b> docs (PDF, DOCX, XLSX, CSV, TXT). Line items auto-parse into suggested valuation inputs.</p>
        <div className="flex flex-wrap gap-2 mt-3 items-center">
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-10 rounded-lg border border-slate-300 px-2.5 text-[13px]" data-testid="intel-kind-select">
            <option value="business_plan">Business plan</option>
            <option value="blueprint">Blueprint / tech spec</option>
            <option value="balance_sheet">Balance Sheet</option>
            <option value="profit_loss">Profit & Loss</option>
            <option value="forecast">Forecast / model</option>
            <option value="cap_table">Cap table</option>
            <option value="document">Other document</option>
          </select>
          <label className="btn-primary-vs h-10 px-4 text-[13px] font-semibold flex items-center gap-1.5 cursor-pointer" data-testid="intel-upload-button"><Upload size={14} /> Choose file<input type="file" className="hidden" accept=".pdf,.docx,.xlsx,.xls,.csv,.txt,.md" onChange={upload} /></label>
          <button onClick={suggest} className="h-10 px-4 text-[13px] font-semibold rounded-[10px] border border-[#174C82] text-[#174C82] flex items-center gap-1.5 hover:bg-[#FFF8EB] transition-colors" data-testid="intel-suggest-button"><Sparkles size={14} /> Apply to valuation inputs</button>
        </div>
        {sugg && (
          <div className="mt-3 rounded-lg bg-[#FFF8EB] border border-[#F5C86B] p-3 text-[13px]" data-testid="intel-suggest-output">
            <div className="font-semibold text-[#174C82]">Suggested inputs ({sugg.items} knowledge items)</div>
            <div className="flex flex-wrap gap-1.5 mt-1.5">{Object.entries(sugg.suggested || {}).map(([k, v]) => <span key={k} className="font-mono-num text-[12px] bg-white border border-[#F5C86B] rounded-md px-2 py-1">{k}: {typeof v === "number" ? v.toLocaleString() : String(v)}</span>)}</div>
            {(sugg.notes || []).map((n, i) => <div key={i} className="text-slate-600 text-[12px] mt-1">• {n}</div>)}
          </div>
        )}
        {lastUpload && ((lastUpload.analysis.flags || []).length > 0 || lastUpload.analysis.unit || (lastUpload.analysis.periods || []).length > 0) && (
          <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-[13px]" data-testid="intel-parse-detail">
            <div className="font-semibold text-slate-700">Parse detail — {lastUpload.filename}</div>
            <div className="flex flex-wrap gap-1.5 mt-1.5 text-[12px]">
              {lastUpload.analysis.unit && <span className="bg-white border border-slate-200 rounded-md px-2 py-1">Scale: {lastUpload.analysis.unit}</span>}
              {(lastUpload.analysis.periods || []).length > 0 && <span className="bg-white border border-slate-200 rounded-md px-2 py-1">Periods: {lastUpload.analysis.periods.join(" · ")}</span>}
              {lastUpload.analysis.latest_period && <span className="bg-white border border-slate-200 rounded-md px-2 py-1">Latest: {lastUpload.analysis.latest_period}</span>}
            </div>
            {lastUpload.analysis.detailed && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {Object.entries(lastUpload.analysis.detailed).slice(0, 10).map(([k, v]) => (
                  <span key={k} className="font-mono-num text-[11px] bg-white border border-slate-200 rounded-md px-2 py-1" title={(v.source_line || "") + ` (${v.period || ""})`}>
                    {k}: {Number(v.value).toLocaleString()} <span className="text-slate-400">· {Math.round((v.confidence || 0) * 100)}%</span>
                  </span>
                ))}
              </div>
            )}
            {(lastUpload.analysis.flags || []).map((f, i) => (
              <div key={i} className={`text-[12px] mt-1.5 px-2.5 py-1.5 rounded-md border ${f.level === "review" ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-[#FFF8EB] border-[#F5C86B] text-[#16294D]"}`}>⚑ {f.msg}</div>
            ))}
          </div>
        )}
      </div>

      {/* knowledge base list */}
      <div className="card-vs p-5">
        <div className="font-semibold text-[15px]">Knowledge base ({items.length})</div>
        {items.length === 0 && <div className="text-[13px] text-slate-500 mt-2">Empty — inspect your website or upload your first document to build valuation context.</div>}
        <div className="mt-2 space-y-2">
          {items.map((it) => (
            <div key={it.id} className="flex items-center gap-2.5 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-[13px]">
              {it.kind === "website" ? <Link2 size={15} className="text-[#174C82] shrink-0" /> : <FileText size={15} className="text-slate-400 shrink-0" />}
              <div className="min-w-0">
                <div className="font-medium truncate">{it.filename || it.domain || it.kind} <span className="ml-1 text-[10px] font-bold text-slate-500 bg-white border border-slate-200 rounded px-1.5 py-0.5">{KIND_LABEL[it.kind] || it.kind}</span></div>
                <div className="text-[11px] text-slate-500 truncate">{it.analysis?.coverage || it.description || it.title || ""}{it.signals?.saas ? " · SaaS signals" : ""}{it.signals?.ai ? " · AI signals" : ""}{it.signals?.enterprise ? " · enterprise" : ""}</div>
              </div>
              <div className="ml-auto flex items-center gap-1 shrink-0">
                {it.content_b64 !== undefined || it.filename ? null : null}
                <button onClick={() => remove(it.id)} className="p-1.5 text-slate-400 hover:text-red-600 transition-colors" data-testid={`intel-delete-${it.id}`}><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* integrations */}
      <div className="card-vs p-5">
        <div className="flex items-center gap-2 font-semibold text-[15px]"><Plug size={17} className="text-[#174C82]" /> CRM & accounting integrations</div>
        <p className="text-[13px] text-slate-500 mt-1">Connect QuickBooks, Xero, Sage, HubSpot, Salesforce and more. One-click OAuth where the platform app is approved, guided setup otherwise — credentials are hashed, never stored raw. Then import statements and map ARR / cash / pipeline into the valuation.</p>
        {new URLSearchParams(window.location.search).get("oauth") === "connected" && (
          <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-[13px] px-4 py-2.5" data-testid="oauth-success-banner">✓ {new URLSearchParams(window.location.search).get("provider")} connected via OAuth — hit Sync now for a live pull.</div>
        )}
        {new URLSearchParams(window.location.search).get("oauth") === "error" && (
          <div className="mt-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-[13px] px-4 py-2.5">OAuth failed for {new URLSearchParams(window.location.search).get("provider")} — use guided connect below.</div>
        )}
        <div className="grid sm:grid-cols-2 gap-2.5 mt-3">
          {providers.map((p) => (
            <div key={p.id} className={`rounded-xl border p-3.5 transition-all ${p.status === "connected" ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200 hover:border-[#EEA415]"}`}>
              <div className="flex items-center gap-2.5">
                <span className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center shrink-0 p-1.5 shadow-sm" title={p.name}>
                  {p.logo_src
                    ? <img src={p.logo_src} alt={`${p.name} logo`} className="w-full h-full object-contain" draggable={false} />
                    : <span className="text-[12px] font-bold" style={{ color: p.brand || "#0E1B33" }}>{p.logo}</span>}
                </span>
                <div className="min-w-0">
                  <div className="font-semibold text-[14px] flex items-center gap-1.5">{p.name}
                    {p.oauth_connected
                      ? <span className="text-[9px] font-black tracking-wider text-white bg-emerald-600 rounded px-1.5 py-0.5">LIVE</span>
                      : p.has_platform_app
                        ? <span className="text-[9px] font-black tracking-wider text-white bg-[#174C82] rounded px-1.5 py-0.5">OAUTH READY</span>
                        : <span className="text-[9px] font-black tracking-wider text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">GUIDED</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 capitalize">{p.kind}{p.last_mode ? ` · last sync: ${p.last_mode}` : ""}</div>
                </div>
                {p.status === "connected"
                  ? <span className="ml-auto text-[11px] font-bold text-emerald-700 flex items-center gap-1"><Check size={13} /> Connected</span>
                  : <button onClick={() => setShowConn(p.id)} className="ml-auto text-[12px] font-semibold text-[#174C82] bg-[#FFF8EB] hover:bg-[#FDEFD2] rounded-md px-2.5 py-1.5 transition-colors" data-testid={`integration-connect-${p.id}`}>Connect</button>}
              </div>
              <div className="text-[12px] text-slate-500 mt-1.5">{p.desc}</div>
              <div className="text-[11px] text-slate-400 mt-1">Pulls: {(p.docs || []).join(" · ")}</div>
              {showConn === p.id && p.status !== "connected" && (
                <div className="mt-2.5 space-y-2 rounded-lg bg-white border border-slate-200 p-2.5">
                  <button onClick={() => oauthGo(p.id)} disabled={oauthBusy === p.id} className="btn-primary-vs w-full h-9 text-[13px] font-semibold flex items-center justify-center gap-1.5" data-testid={`integration-oauth-${p.id}`}>
                    {oauthBusy === p.id ? <Loader2 size={14} className="animate-spin" /> : <Plug size={14} />} {p.has_platform_app ? "Connect with OAuth" : "Try OAuth / show setup"}
                  </button>
                  {oauthInfo && oauthInfo.pid === p.id && oauthInfo.mode === "guided" && (
                    <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5 text-[12px] text-slate-600" data-testid={`oauth-setup-${p.id}`}>
                      <div className="font-semibold text-slate-700">Connect {p.name} — one-time setup</div>
                      <ol className="list-decimal ml-4 mt-1 space-y-0.5">
                        <li>Open <a href={p.dev_docs} target="_blank" rel="noreferrer" className="text-[#174C82]">developer docs</a> and create an OAuth app.</li>
                        <li>Set the redirect URI to:<div className="font-mono-num text-[10px] bg-white border border-slate-200 rounded px-1.5 py-1 mt-1 break-all">{oauthInfo.callback_url}</div></li>
                        <li>Request scopes: <span className="font-mono-num text-[10px]">{(oauthInfo.scopes || []).join(", ")}</span></li>
                        <li>Ask us to add <span className="font-mono-num text-[10px]">{oauthInfo.env_hint}</span> — then this button becomes one-click OAuth.</li>
                      </ol>
                    </div>
                  )}
                  <div className="text-[11px] font-semibold text-slate-400 text-center">— or guided connect —</div>
                  <input value={connForm.account_label} onChange={(e) => setConnForm({ ...connForm, account_label: e.target.value })} placeholder="Account label (e.g. Acme QuickBooks)" className="w-full h-9 rounded-lg border border-slate-300 px-2.5 text-[13px]" data-testid={`integration-label-${p.id}`} />
                  {(p.fields || []).slice(0, 2).map((f) => (
                    <input key={f} value={connForm.credentials[f] || ""} onChange={(e) => setConnForm({ ...connForm, credentials: { ...connForm.credentials, [f]: e.target.value } })} placeholder={`${f} (optional with label)`} className="w-full h-9 rounded-lg border border-slate-300 px-2.5 text-[13px]" data-testid={`integration-field-${p.id}-${f}`} />
                  ))}
                  <div className="flex gap-2">
                    <button onClick={() => connect(p.id)} className="flex-1 h-9 text-[13px] font-semibold rounded-[10px] border border-[#174C82] text-[#174C82] hover:bg-[#FFF8EB] transition-colors" data-testid={`integration-confirm-${p.id}`}>Confirm guided connect</button>
                    <button onClick={() => setShowConn(null)} className="h-9 px-3 text-[13px] text-slate-500">Cancel</button>
                  </div>
                </div>
              )}
              {p.status === "connected" && (
                <div className="flex gap-2 mt-2.5 flex-wrap">
                  <button onClick={() => sync(p.id)} className="text-[12px] font-semibold text-[#174C82] bg-[#FFF8EB] hover:bg-[#FDEFD2] rounded-md px-2.5 py-1.5 transition-colors" data-testid={`integration-sync-${p.id}`}>{p.oauth_connected ? "Live sync" : "Sync now"}</button>
                  {!p.oauth_connected && <button onClick={() => oauthGo(p.id)} className="text-[12px] font-semibold text-slate-600 border border-slate-200 hover:border-[#EEA415] rounded-md px-2.5 py-1.5 transition-colors" data-testid={`integration-upgrade-oauth-${p.id}`}>Enable live pull</button>}
                  <button onClick={() => disconnect(p.id)} className="text-[12px] text-slate-400 hover:text-red-600 px-2 py-1.5 transition-colors" data-testid={`integration-disconnect-${p.id}`}>Disconnect</button>
                  {p.last_sync && <span className="text-[11px] text-slate-400 self-center">· synced {String(p.last_sync).slice(0, 10)}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
